import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  ArrowLeft, 
  AlertCircle, 
  Calendar, 
  Clock, 
  User, 
  File, 
  Info,
  MessageSquare,
  Filter,
  Share,
  Download,
  Printer,
  Eye,
  MoreHorizontal,
  ChevronRight,
  Timer,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Check,
  X,
  CheckCircle,
} from 'lucide-react';

import completedSurveyService from '../../services/completedSurveyService';
import Tooltip from '../Shared/Tooltip';
import { formatDate, formatDateTime, calculateDuration } from '../../utils/dateUtils';
import { SuspendedBadge } from '../common/ParticipantStatusBadge';
import PageLoader from '../ui/PageLoader';

const OPTIONAL_NO_RESPONSE_TOOLTIP = 'This question was optional. The participant did not provide a response.';

// Helper function to format duration from seconds
const formatDurationFromSeconds = (seconds) => {
  if (!seconds || seconds < 0) return 'N/A';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

// Helper function to determine question type based on answer format
function determineQuestionType(answerValue) {
  if (typeof answerValue === 'number') {
    return 'numeric';
  } else if (answerValue === 'true' || answerValue === 'false' || answerValue === true || answerValue === false) {
    return 'yes_no';
  } else if (answerValue && typeof answerValue === 'string' && answerValue.includes('-') && answerValue.split('-').length === 3) {
    // Simple pattern matching for dates (YYYY-MM-DD)
    return 'date';
  }
  
  return 'text';
}

// Helper function to format question type for display
function formatQuestionType(type) {
  if (!type) return 'Unknown';
  
  // Map of backend types to display labels
  const typeMap = {
    'text': 'Text',
    'numeric': 'Numeric',
    'date': 'Date',
    'yes_no': 'Yes/No',
    'multiple_choice': 'Multiple Choice',
    'checkbox': 'Checkbox',
    'rating': 'Rating Scale',
    'likert': 'Ordinal',
    'email': 'Email',
    'time': 'Time',
    'datetime': 'Date & Time',
    'file': 'File Upload',
    'signature': 'Signature',
    'info': 'Information',
    'ordinal': 'Ordinal',
    'nominal': 'Nominal',
    'boolean': 'Yes/No',
    'vas': 'Visual Analog Scale',
    'nrs': 'Numeric Rating Scale',
    'dropdown': 'Dropdown'
  };
  
  // If type exists in our map, return formatted version, otherwise capitalize first letter
  return typeMap[type.toLowerCase()] || 
    type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
}

const CompletedSurveyDetails = () => {
  const { participantID, studyId, siteId, surveyId } = useParams();
  const navigate = useNavigate();
  const [surveyDetails, setSurveyDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('all');
  const [showActions, setShowActions] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);
  const [participantHumanId, setParticipantHumanId] = useState(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  useEffect(() => {
    const fetchSurveyDetails = async () => {
      try {
        setLoading(true);
        console.log(`Fetching details for survey ID: ${surveyId}`);
        const data = await completedSurveyService.getCompletedSurveyDetails(surveyId);
        console.log('Received survey details:', data);
        
        // Clean up data before setting state
        // Remove email for privacy reasons
        if (data.participant_email) {
          delete data.participant_email;
        }
        
        setSurveyDetails(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching survey details:', err);
        const errorMessage = err.response?.data?.error || 'Failed to load survey details. Please try again later.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (surveyId) {
      fetchSurveyDetails();
    }

    // Add scroll event listener
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [surveyId]);

  // No duplicate useEffect needed here
  
  // Log survey details when they change and extract participant ID
  useEffect(() => {
    if (surveyDetails) {
      console.log('Survey Details loaded:', surveyDetails);
      console.log('is_suspended value:', surveyDetails.is_suspended);
      console.log('status value:', surveyDetails.status);

      // PRIORITY 1: Get the human-readable participant ID from the API response (most reliable)
      if (surveyDetails.participant_human_id) {
        // Use the human-readable ID directly from the API response
        setParticipantHumanId(surveyDetails.participant_human_id);
        console.log('Using participant_human_id from API:', surveyDetails.participant_human_id);
        return; // Early return - we found it!
      } else if (surveyDetails.participant_details && surveyDetails.participant_details.participant_id) {
        // Fallback 1: From participant_details object (from SimpleParticipantSerializer)
        setParticipantHumanId(surveyDetails.participant_details.participant_id);
        console.log('Using participant_id from participant_details');
      } else if (surveyDetails.participant_email) {
        // Fallback 2: Extract from email if available (older format)
        const emailParts = surveyDetails.participant_email.split('@');
        if (emailParts.length > 0) {
          setParticipantHumanId(emailParts[0]); // Usually in format 1002@site.com
          console.log('Extracted participant ID from email');
        }
      } else if (surveyDetails.metadata && surveyDetails.metadata.participant_id) {
        // Fallback 3: Check metadata
        setParticipantHumanId(surveyDetails.metadata.participant_id);
        console.log('Using participant_id from metadata');
      } else {
        // Last resort: Show a message that we couldn't find the human-readable ID
        setParticipantHumanId('ID not available');
        console.warn('Could not find human-readable participant ID in response');
      }
    }
  }, [surveyDetails, participantID]); // Run when surveyDetails or participantID changes

  const handleBack = () => {
    // Route params when on the scoped URL; otherwise the external UUIDs from
    // the details API, so the unscoped /completed-surveys/:id entry point
    // (e.g. from a notification) still lands on the participant. Study/site
    // pages only resolve external UUIDs, so int ids are never used here.
    const s = studyId || surveyDetails?.study_external_id;
    const si = siteId || surveyDetails?.site_external_id;
    const p = participantID || surveyDetails?.participant_id;
    if (s && si && p) {
      navigate(`/studies/${s}/sites/${si}/participant/${p}`);
    } else {
      window.history.back();
    }
  };

  // Filter questions based on active section
  const getFilteredQuestions = () => {
    // First check if we have questions array
    if (!surveyDetails?.questions || surveyDetails.questions.length === 0) {
      if (surveyDetails?.responses && Object.keys(surveyDetails.responses).length > 0) {
        return [];
      }
      return [];
    }
    
    if (activeSection === 'all') {
      return surveyDetails.questions || [];
    }

    return surveyDetails.questions.filter(question => {
      switch (activeSection) {
        case 'text':
          return ['text', 'email'].includes(question.type);
        case 'choice':
          return ['multiple_choice', 'checkbox', 'yes_no', 'nominal', 'ordinal', 'boolean', 'dropdown'].includes(question.type);
        case 'numeric':
          return ['rating', 'numeric', 'vas', 'nrs', 'likert'].includes(question.type);
        case 'date':
          return ['date'].includes(question.type);
        default:
          return true;
      }
    });
  };

  // Get icon for question type
  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case 'text':
      case 'email':
        return <MessageSquare className="w-4 h-4" />;
      case 'multiple_choice':
      case 'checkbox':
      case 'nominal':
      case 'ordinal':
      case 'dropdown':
        return <CheckCircle className="w-4 h-4" />;
      case 'yes_no':
      case 'boolean':
        return <Check className="w-4 h-4" />;
      case 'rating':
      case 'numeric':
      case 'nrs':
      case 'vas':
      case 'likert':
        return <Eye className="w-4 h-4" />;
      case 'date':
        return <Calendar className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  // Get color for question type
  const getQuestionTypeColor = (type) => {
    switch (type) {
      case 'text':
        return 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300';
      case 'email':
        return 'bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-300';
      case 'multiple_choice':
        return 'bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-300';
      case 'checkbox':
        return 'bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-500/10 dark:text-fuchsia-300';
      case 'nominal':
        return 'bg-pink-50 text-pink-600 dark:bg-pink-500/10 dark:text-pink-300';
      case 'dropdown':
        return 'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300';
      case 'yes_no':
      case 'boolean':
        return 'bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-300';
      case 'rating':
        return 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300';
      case 'numeric':
        return 'bg-yellow-50 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-300';
      case 'nrs':
        return 'bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-300';
      case 'vas':
        return 'bg-lime-50 text-lime-700 dark:bg-lime-500/10 dark:text-lime-300';
      case 'likert':
        return 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300';
      case 'ordinal':
        return 'bg-cyan-50 text-cyan-600 dark:bg-cyan-500/10 dark:text-cyan-300';
      case 'date':
        return 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-white/[0.06] dark:text-gray-300';
    }
  };

  // Get formatted answer value for the table view
  // Helper: resolve answer code to label using question options
  const resolveAnswerLabel = (answer, options) => {
    if (!options || answer === null || answer === undefined) return answer;
    if (Array.isArray(options)) {
      for (const opt of options) {
        if (typeof opt === 'object') {
          if (String(opt.value) === String(answer) || String(opt.label) === String(answer)) {
            return opt.label || opt.value;
          }
        }
      }
    }
    return answer;
  };

  const getFormattedTableAnswer = (question) => {
    const { type, answer, options } = question;
    
    if (answer === null || answer === undefined) {
      // Check if this is a partial/suspended survey
      if (surveyDetails?.is_suspended && surveyDetails?.has_partial_responses) {
        return (
          <span className="text-gray-400 dark:text-gray-500 italic">
            Not answered (participant deactivated)
          </span>
        );
      }
      if (question.is_required === false) {
        return (
          <Tooltip content={OPTIONAL_NO_RESPONSE_TOOLTIP} position="top" showIcon={false}>
            <span className="text-gray-400 dark:text-gray-500 italic">No response (optional)</span>
          </Tooltip>
        );
      }
      return 'No answer provided';
    }

    let displayAnswer = answer;
    
    if (answer && typeof answer === 'object' && answer.answer !== undefined) {
      displayAnswer = answer.answer;
    }
    
    switch (type) {
      case 'yes_no':
      case 'boolean': {
        const isYes = displayAnswer === 'Yes' || displayAnswer === 'yes' || displayAnswer === true || displayAnswer === 1 || displayAnswer === '1';
        const isNo = displayAnswer === 'No' || displayAnswer === 'no' || displayAnswer === false || displayAnswer === 0 || displayAnswer === '0';
        // Handle dict options (stored as {type:'boolean', yesLabel:'...', noLabel:'...'})
        if (options && !Array.isArray(options) && typeof options === 'object') {
          if (isYes) return options.yesLabel || 'Yes';
          if (isNo) return options.noLabel || 'No';
        }
        if (isYes && options && Array.isArray(options) && options[0]) return typeof options[0] === 'object' ? (options[0].label || 'Yes') : 'Yes';
        if (isNo && options && Array.isArray(options) && options[1]) return typeof options[1] === 'object' ? (options[1].label || 'No') : 'No';
        return isYes ? 'Yes' : isNo ? 'No' : String(displayAnswer);
      }
      
      case 'rating':
      case 'numeric':
      case 'nrs':
        return String(displayAnswer);

      case 'vas':
        return typeof displayAnswer === 'number' ? `${displayAnswer.toFixed(1)}%` : String(displayAnswer);
        
      case 'date':
        return formatDate(displayAnswer);
        
      case 'multiple_choice':
      case 'nominal':
      case 'ordinal':
      case 'likert':
      case 'dropdown':
      case 'checkbox':
        if (Array.isArray(displayAnswer)) {
          return displayAnswer.map(a => resolveAnswerLabel(a, options)).join(', ');
        }
        return String(resolveAnswerLabel(displayAnswer, options));
        
      default:
        return typeof displayAnswer === 'object' ? JSON.stringify(displayAnswer) : String(displayAnswer);
    }
  };

  // Get detailed answer component for expanded view
  const renderDetailedAnswer = (question) => {
    // Instead of destructuring, get the properties directly
    const { id, type, answer } = question;
    
    if (answer === null || answer === undefined) {
      if (question.is_required === false) {
        return (
          <Tooltip content={OPTIONAL_NO_RESPONSE_TOOLTIP} position="top" showIcon={false}>
            <div className="text-gray-400 dark:text-gray-500 italic mt-2">No response (optional)</div>
          </Tooltip>
        );
      }
      return (
        <div className="text-gray-500 dark:text-gray-400 italic mt-2">No answer provided</div>
      );
    }
    
    // Extract timestamp and actual answer from the answer object
    let timestamp = null;
    let displayAnswer = answer;
    
    // Check if answer has timestamp directly
    if (answer && typeof answer === 'object') {
      // If answer has a timestamp property, create the timestamp element
      if (answer.timestamp) {
        timestamp = (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            <span>Answered at: {formatDateTime(answer.timestamp)}</span>
          </div>
        );
      }
      
      // If answer has an answer property, use it as the display answer
      if (answer.answer !== undefined) {
        displayAnswer = answer.answer;
      }
    }

    switch (type) {
      case 'text':
      case 'email':
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
            {displayAnswer}
            {timestamp}
          </div>
        );
      
      case 'multiple_choice':
        // Check if options array exists to create a better UI
        if (question.options && Array.isArray(question.options)) {
          return (
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20">
              <div className="grid gap-2">
                {(() => {
                  // Match only the first option carrying the saved value/label —
                  // duplicate values are allowed and can't be disambiguated from the
                  // stored answer, so highlight a single option (FECOA-1521).
                  const selectedIdx = question.options.findIndex((option) => {
                    const oLabel = typeof option === 'object' ? (option.label || option.value || '') : option;
                    const oValue = typeof option === 'object' ? (option.value !== undefined ? option.value : option.label) : option;
                    return String(oValue) === String(displayAnswer) || oLabel === displayAnswer;
                  });
                  return question.options.map((option, idx) => {
                  // Handle both string options and {label, value} object options
                  const optionLabel = typeof option === 'object' ? (option.label || option.value || '') : option;
                  const isSelected = idx === selectedIdx;

                  return (
                    <div key={idx} className={`flex items-center p-2 rounded-md ${isSelected ? 'bg-purple-50 border border-purple-200 dark:bg-purple-500/10 dark:border-purple-500/30' : 'bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-700'}`}>
                      <div className={`h-3 w-3 rounded-full mr-3 flex-shrink-0 ${isSelected ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                      <span className={isSelected ? 'font-medium' : ''}>{optionLabel}</span>
                      {isSelected && <Check className="w-4 h-4 text-purple-500 ml-auto" />}
                    </div>
                  );
                  });
                })()}
              </div>
              {timestamp}
            </div>
          );
        }

        // Fallback if no options array - handle object displayAnswer
        const displayAnswerText = typeof displayAnswer === 'object' 
          ? (displayAnswer.label || displayAnswer.value || JSON.stringify(displayAnswer))
          : displayAnswer;
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center p-2 rounded-md bg-purple-50 border border-purple-200 dark:bg-purple-500/10 dark:border-purple-500/30">
              <div className="h-3 w-3 rounded-full bg-purple-500 mr-3"></div>
              <span className="font-medium">{displayAnswerText}</span>
            </div>
            {timestamp}
          </div>
        );
      
      case 'checkbox':
        if (Array.isArray(displayAnswer)) {
          return (
            <div className="space-y-2">
              {displayAnswer.map((item, idx) => (
                <div key={idx} className="flex items-center bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
                  <Check className="w-4 h-4 text-primary-500 mr-2" />
                  <span className="text-gray-800 dark:text-gray-100">{item}</span>
                </div>
              ))}
              {timestamp}
            </div>
          );
        }
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 flex items-center text-gray-800 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20">
            {String(displayAnswer) === 'true' ? (
              <Check className="w-4 h-4 text-green-500 mr-2" />
            ) : (
              <X className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
            )}
            {String(displayAnswer)}
            {timestamp}
          </div>
        );
      
      case 'rating':
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center mb-2">
              <span className="bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 text-lg font-medium rounded-md px-3 py-1 mr-2">
                {displayAnswer}
              </span>
              {question.options?.min_value && question.options?.max_value && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  (Scale: {question.options.min_value} to {question.options.max_value})
                </span>
              )}
            </div>
            
            {question.options?.min_value && question.options?.max_value && (
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="h-2 bg-amber-500 rounded-full" 
                  style={{ 
                    width: `${((displayAnswer - question.options.min_value) / (question.options.max_value - question.options.min_value)) * 100}%` 
                  }}
                ></div>
              </div>
            )}
            {timestamp}
          </div>
        );
      
      case 'likert':
        // Resolve numeric answer to label using question.options array
        const ordinalOptions = question.options && Array.isArray(question.options) ? question.options : [];
        let selectedLabel = String(displayAnswer);
        let selectedIdx = -1;
        // Match only the first option carrying the saved value — duplicate values
        // are allowed and the stored answer can't disambiguate beyond the value
        // itself, so highlight a single option rather than all matches (FECOA-1521).
        ordinalOptions.forEach((opt, idx) => {
          const optValue = typeof opt === 'object' ? opt.value : opt;
          if (selectedIdx === -1 && String(optValue) === String(displayAnswer)) {
            selectedLabel = typeof opt === 'object' ? (opt.label || String(opt.value)) : String(opt);
            selectedIdx = idx;
          }
        });
        
        if (ordinalOptions.length > 0) {
          return (
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
              <div className="grid gap-2">
                {ordinalOptions.map((option, idx) => {
                  const optionLabel = typeof option === 'object' ? (option.label || option.value || '') : option;
                  const isSelected = idx === selectedIdx;
                  return (
                    <div key={idx} className={`flex items-center p-2 rounded-md ${isSelected ? 'bg-cyan-50 border border-cyan-200 dark:bg-cyan-500/10 dark:border-cyan-500/30' : 'bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-700'}`}>
                      <div className={`h-3 w-3 rounded-full mr-3 flex-shrink-0 ${isSelected ? 'bg-cyan-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                      <span className={isSelected ? 'font-medium' : ''}>{optionLabel}</span>
                      {isSelected && <Check className="w-4 h-4 text-cyan-500 ml-auto" />}
                    </div>
                  );
                })}
              </div>
              {selectedIdx !== -1 && (
                <div className="mt-3">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="h-2 bg-cyan-500 rounded-full" 
                      style={{ width: `${(selectedIdx / (ordinalOptions.length - 1)) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>{typeof ordinalOptions[0] === 'object' ? ordinalOptions[0].label : ordinalOptions[0]}</span>
                    <span>{typeof ordinalOptions[ordinalOptions.length - 1] === 'object' ? ordinalOptions[ordinalOptions.length - 1].label : ordinalOptions[ordinalOptions.length - 1]}</span>
                  </div>
                </div>
              )}
              {timestamp}
            </div>
          );
        }
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="font-medium text-gray-800 dark:text-gray-100">{selectedLabel}</div>
            {timestamp}
          </div>
        );
      
      case 'yes_no':
      case 'boolean': {
        // Handle all variations of Yes/No answers including numeric 1/0
        const isYesDetailed = displayAnswer === 'Yes' || displayAnswer === 'yes' || displayAnswer === true || displayAnswer === 1 || displayAnswer === '1';
        const isNoDetailed = displayAnswer === 'No' || displayAnswer === 'no' || displayAnswer === false || displayAnswer === 0 || displayAnswer === '0';
        // Resolve custom label from dict options (e.g. {type:'boolean', yesLabel:'Yes sir', noLabel:'No sir'})
        let boolLabel = String(displayAnswer);
        if (question.options && !Array.isArray(question.options) && typeof question.options === 'object') {
          if (isYesDetailed) boolLabel = question.options.yesLabel || 'Yes';
          else if (isNoDetailed) boolLabel = question.options.noLabel || 'No';
        } else if (question.options && Array.isArray(question.options)) {
          if (isYesDetailed && question.options[0]) boolLabel = typeof question.options[0] === 'object' ? (question.options[0].label || 'Yes') : 'Yes';
          else if (isNoDetailed && question.options[1]) boolLabel = typeof question.options[1] === 'object' ? (question.options[1].label || 'No') : 'No';
        } else {
          boolLabel = isYesDetailed ? 'Yes' : isNoDetailed ? 'No' : String(displayAnswer);
        }
        
        return (
          <div>
            <div className={`inline-flex items-center px-4 py-2 rounded-md shadow-sm dark:shadow-gray-900/20 ${
              isYesDetailed ? 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/30' : 
              isNoDetailed ? 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30' : 
              'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700'
            }`}>
              {isYesDetailed ? (
                <Check className="w-4 h-4 mr-2" />
              ) : isNoDetailed ? (
                <X className="w-4 h-4 mr-2" />
              ) : (
                <AlertCircle className="w-4 h-4 mr-2" />
              )}
              <span className="font-medium">{boolLabel}</span>
            </div>
            {timestamp}
          </div>
        );
      }
      
      case 'numeric': {
        // Options use camelCase: minValue, maxValue, leftAnchor, rightAnchor, step
        const nMin = question.options?.minValue ?? question.options?.min_value ?? 0;
        const nMax = question.options?.maxValue ?? question.options?.max_value ?? 10;
        const nStep = question.options?.step ?? 1;
        const nLeftAnchor = question.options?.leftAnchor || question.options?.left_anchor || '';
        const nRightAnchor = question.options?.rightAnchor || question.options?.right_anchor || '';
        const nValue = typeof displayAnswer === 'number' ? displayAnswer : parseFloat(displayAnswer) || 0;
        const nScaleNumbers = [];
        for (let n = nMin; n <= nMax; n += nStep) {
          nScaleNumbers.push(n);
        }
        
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex flex-wrap gap-2 justify-center">
              {nScaleNumbers.map((num) => (
                <div
                  key={num}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                    num === nValue
                      ? 'bg-yellow-500 text-white border-2 border-yellow-600'
                      : 'bg-gray-100 dark:bg-gray-900 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {num}
                </div>
              ))}
            </div>
            {(nLeftAnchor || nRightAnchor) && (
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mt-2">
                <span>{nLeftAnchor}</span>
                <span>{nRightAnchor}</span>
              </div>
            )}
            {timestamp}
          </div>
        );
      }
      case 'vas': {
        // Options use camelCase: minValue, maxValue, leftAnchor, rightAnchor
        const vasMin = question.options?.minValue ?? question.options?.min_value ?? 0;
        const vasMax = question.options?.maxValue ?? question.options?.max_value ?? 100;
        const vasLeftAnchor = question.options?.leftAnchor || question.options?.left_anchor || '';
        const vasRightAnchor = question.options?.rightAnchor || question.options?.right_anchor || '';
        const vasValue = typeof displayAnswer === 'number' ? displayAnswer : parseFloat(displayAnswer) || 0;
        const vasPercent = ((vasValue - vasMin) / (vasMax - vasMin)) * 100;
        
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="text-center mb-3">
              <span className="bg-lime-100 text-lime-800 dark:bg-lime-500/15 dark:text-lime-300 text-lg font-medium rounded-md px-3 py-1">
                {vasValue.toFixed(1)}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                / {vasMax}
              </span>
            </div>
            <div className="relative w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full">
              <div
                className="absolute inset-y-0 left-0 bg-lime-500/70 rounded-full"
                style={{ width: `${vasPercent}%` }}
              ></div>
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white dark:bg-gray-800 border-2 border-lime-600 rounded-full shadow-sm"
                style={{ left: `calc(${vasPercent}% - 10px)` }}
              ></div>
            </div>
            {(vasLeftAnchor || vasRightAnchor) && (
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mt-2">
                <span>{vasLeftAnchor}</span>
                <span>{vasRightAnchor}</span>
              </div>
            )}
            {timestamp}
          </div>
        );
      }
      case 'date':
        return (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 rounded-md border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center mb-2">
              <Calendar className="w-4 h-4 text-indigo-500 mr-2" />
              <span className="text-gray-800 dark:text-gray-100 font-medium">{formatDate(displayAnswer)}</span>
            </div>
            {timestamp}
          </div>
        );
      
      case 'ordinal':
      case 'nominal': {
        // Build a set of selected values for quick lookup
        const selectedValues = new Set(
          Array.isArray(displayAnswer)
            ? displayAnswer.map(v => String(v))
            : [String(displayAnswer)]
        );
        // Show all options with selected/unselected distinction
        if (question.options && Array.isArray(question.options)) {
          return (
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
              <div className="grid gap-2">
                {question.options.map((option, idx) => {
                  const optionLabel = typeof option === 'object' ? (option.label || option.value || '') : option;
                  const optionValue = typeof option === 'object' ? (option.value !== undefined ? option.value : option.label) : option;
                  const isSelected = selectedValues.has(String(optionValue));
                  // Full class names (not built from a colour name) so Tailwind keeps them.
                  const accent = type === 'ordinal'
                    ? { row: 'bg-cyan-50 border border-cyan-200 dark:bg-cyan-500/10 dark:border-cyan-500/30', icon: 'text-cyan-500' }
                    : { row: 'bg-pink-50 border border-pink-200 dark:bg-pink-500/10 dark:border-pink-500/30', icon: 'text-pink-500' };
                  return (
                    <div key={idx} className={`flex items-center p-2 rounded-md ${isSelected ? accent.row : 'bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-700'}`}>
                      {isSelected ? (
                        <Check className={`w-4 h-4 ${accent.icon} mr-3 flex-shrink-0`} />
                      ) : (
                        <X className="w-4 h-4 text-gray-300 dark:text-gray-600 mr-3 flex-shrink-0" />
                      )}
                      <span className={isSelected ? 'font-medium text-gray-800 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}>{optionLabel}</span>
                    </div>
                  );
                })}
              </div>
              {timestamp}
            </div>
          );
        }
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center p-2 rounded-md bg-primary-50 border border-primary-200 dark:bg-primary-500/10 dark:border-primary-500/30">
              <div className="h-3 w-3 rounded-full bg-primary-500 mr-3"></div>
              <span className="font-medium text-gray-800 dark:text-gray-100">{typeof displayAnswer === 'object' ? (displayAnswer.label || displayAnswer.value) : resolveAnswerLabel(displayAnswer, question.options)}</span>
            </div>
            {timestamp}
          </div>
        );
      }
      case 'dropdown': {
        const dropdownLabel = typeof displayAnswer === 'object' 
          ? (displayAnswer.label || displayAnswer.value || JSON.stringify(displayAnswer))
          : displayAnswer;
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center p-2 rounded-md bg-indigo-50 border border-indigo-200 dark:bg-indigo-500/10 dark:border-indigo-500/30">
              <ChevronDown className="w-4 h-4 text-indigo-500 mr-2" />
              <span className="font-medium text-gray-800 dark:text-gray-100">{dropdownLabel}</span>
            </div>
            {timestamp}
          </div>
        );
      }
      default:
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20">
            {String(displayAnswer)}
            {timestamp}
          </div>
        );
    }
  };

  // Toggle expanded view for a question
  const toggleExpandQuestion = (questionId) => {
    if (expandedQuestionId === questionId) {
      setExpandedQuestionId(null);
    } else {
      setExpandedQuestionId(questionId);
    }
  };

  // Loading state
  if (loading) {
    return <PageLoader label="Loading survey" className="min-h-[60vh]" />;
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="card p-8 max-w-md w-full"
        >
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-danger-50 dark:bg-danger-500/10 mx-auto mb-5">
            <AlertCircle className="h-7 w-7 text-danger-500 dark:text-danger-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 text-center mb-2">Unable to Load Survey</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-6">{error}</p>
          <button 
            onClick={handleBack}
            className="btn btn-primary w-full"
          >
            <ArrowLeft className="h-4 w-4" />
            Return to Previous Page
          </button>
        </motion.div>
      </div>
    );
  }

  // Survey not found state
  if (!surveyDetails) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="card p-8 max-w-md w-full"
        >
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 dark:bg-amber-500/10 mx-auto mb-5">
            <AlertCircle className="h-7 w-7 text-amber-500 dark:text-amber-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 text-center mb-2">Survey Not Found</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-6">We couldn't locate the survey you're looking for. It may have been deleted or moved.</p>
          <button 
            onClick={handleBack}
            className="btn btn-primary w-full"
          >
            <ArrowLeft className="h-4 w-4" />
            Return to Previous Page
          </button>
        </motion.div>
      </div>
    );
  }

  // Filter questions based on active section
  const filteredQuestions = getFilteredQuestions();

  // Internal PKs for troubleshooting (URL fallbacks on participant > survey routes)
  const resolvedStudyIdForDebug =
    surveyDetails.study_id ??
    (studyId != null && studyId !== '' ? studyId : undefined) ??
    surveyDetails.metadata?.study_id;
  const resolvedSiteIdForDebug =
    surveyDetails.site_id ??
    (siteId != null && siteId !== '' ? siteId : undefined) ??
    surveyDetails.metadata?.site_id;
  // Business identifiers from API (Study.code, Site.site_code — same fields as site list / DB)
  const resolvedStudyCodeForDebug =
    surveyDetails.study_code ?? surveyDetails.metadata?.study_code;
  const resolvedSiteCodeForDebug =
    surveyDetails.site_code ?? surveyDetails.metadata?.site_code;

  return (
    <div>
      {/* Header that appears on scroll. It starts at the sidebar's edge on
          large screens (--app-sidebar-width is set by the layout). */}
      <motion.div 
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: scrolled ? 1 : 0, y: scrolled ? 0 : -8 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="fixed top-0 left-0 lg:left-[var(--app-sidebar-width)] right-0 z-20 border-b border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900"
        style={{ pointerEvents: scrolled ? 'auto' : 'none' }}
      >
        <div className="flex items-center justify-between gap-4 px-4 py-2.5 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center">
            <button
              onClick={handleBack}
              className="btn btn-ghost btn-icon mr-2 -ml-2"
            >
              <ArrowLeft className="h-[18px] w-[18px]" />
            </button>
            <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate max-w-xl">
              {surveyDetails.survey_name}
            </h1>
          </div>
          <div className="flex items-center gap-1">
            <button className="btn btn-ghost btn-icon">
              <Share className="h-[18px] w-[18px]" />
            </button>
            <button className="btn btn-ghost btn-icon">
              <Download className="h-[18px] w-[18px]" />
            </button>
            <div className="relative">
              <button 
                onClick={() => setShowActions(!showActions)}
                className="btn btn-ghost btn-icon"
              >
                <MoreHorizontal className="h-[18px] w-[18px]" />
              </button>
              
              {showActions && (
                <motion.div 
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute right-0 top-full z-30 mt-1.5 w-48 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-float dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="py-1">
                    <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-white/[0.06]">
                      <Download className="h-4 w-4 mr-3 text-gray-400 dark:text-gray-500" />
                      Export PDF
                    </button>
                    <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-white/[0.06]">
                      <Printer className="h-4 w-4 mr-3 text-gray-400 dark:text-gray-500" />
                      Print
                    </button>
                    <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-white/[0.06]">
                      <Share className="h-4 w-4 mr-3 text-gray-400 dark:text-gray-500" />
                      Share
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      
      <div>
        {/* Back button */}
        <button
          onClick={handleBack}
          className="back-link"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back to Participant</span>
        </button>
        
        {/* Survey header card */}
        <motion.div 
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="card overflow-hidden mb-6"
        >
          <div className="card-band">
            <h2 className="card-band-title">
              {surveyDetails.survey_name}
            </h2>
          </div>
          
          {/* Info bar - light: primary tint; dark: neutral surface so text isn’t green-on-green */}
          <div className="px-6 py-4 bg-primary-50 text-primary-900 dark:bg-gray-800/90 dark:text-gray-100 dark:border-b dark:border-gray-700 text-sm flex flex-wrap gap-4 md:gap-6">
            <div className="flex items-center">
              <span className="font-semibold mr-2 text-primary-900 dark:text-gray-400">Status:</span>
              <span className="capitalize text-primary-900 dark:text-gray-100">{surveyDetails.status.replace(/_/g, ' ')}</span>
              {surveyDetails.is_suspended && <SuspendedBadge className="ml-2" />}
            </div>
            
            <div className="flex items-center">
              <span className="font-semibold mr-2 text-primary-900 dark:text-gray-400">Started:</span>
              <span className="text-primary-900 dark:text-gray-100">{surveyDetails.openedAt ? formatDateTime(surveyDetails.openedAt) : 'N/A'}</span>
            </div>
            
            <div className="flex items-center">
              <span className="font-semibold mr-2 text-primary-900 dark:text-gray-400">Completed:</span>
              <span className="text-primary-900 dark:text-gray-100">{surveyDetails.completedAt ? formatDateTime(surveyDetails.completedAt) : 'N/A'}</span>
            </div>
            
            <div className="flex items-center">
              <span className="font-semibold mr-2 text-primary-900 dark:text-gray-400">Duration:</span>
              <span className="text-primary-900 dark:text-gray-100">
                {surveyDetails.duration_seconds 
                  ? formatDurationFromSeconds(surveyDetails.duration_seconds)
                  : surveyDetails.openedAt && surveyDetails.completedAt 
                    ? calculateDuration(surveyDetails.openedAt, surveyDetails.completedAt) 
                    : 'N/A'}
              </span>
            </div>
          </div>
          
          <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <div className="rounded-full bg-primary-50 p-3 mr-4 dark:bg-primary-500/10">
                <User className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Participant</div>
                <div className="text-gray-900 dark:text-gray-100 font-medium">
                  {/* Display human-readable participant ID if available, otherwise show 'Loading...' */}
                  {participantHumanId || 'Loading...'}
                </div>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="rounded-full bg-primary-50 p-3 mr-4 dark:bg-primary-500/10">
                <Clock className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Schedule Type</div>
                <div className="text-gray-900 dark:text-gray-100 font-medium capitalize">
                  {(surveyDetails.metadata && surveyDetails.metadata.schedule_type
                    ? surveyDetails.metadata.schedule_type
                    : surveyDetails.schedule_type).replace(/_/g, ' ')}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Partial Completion Warning Banner */}
        {surveyDetails.is_suspended && surveyDetails.questions && surveyDetails.questions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-500/10"
          >
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-1">Partial Survey - Suspended Due to Participant Deactivation</h3>
                <p className="text-sm text-amber-800 dark:text-amber-200/90">
                  This survey was suspended when the participant was deactivated. The responses shown below were captured before suspension and are marked with Partial status. 
                  All responses include item-level timestamps. Data corrections can be handled through the Data Change Request (DCR) workflow.
                </p>
                <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                  <span className="font-medium">Note:</span> Partial responses are excluded from scoring and endpoint derivation by default.
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Responses Section */}
        <motion.div 
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="card overflow-hidden mb-6"
        >
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center">
              <MessageSquare className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Responses</h2>
              <span className="badge badge-brand ml-2 tabular-nums">
                {surveyDetails.questions ? surveyDetails.questions.length : 0}
              </span>
              {surveyDetails.is_suspended && surveyDetails.questions && surveyDetails.questions.length > 0 && (
                <span className="badge badge-warning ml-2">
                  Partial
                </span>
              )}
            </div>
            
            {surveyDetails.questions && surveyDetails.questions.length > 3 && (
              <div className="flex">
                <div className="segmented">
                  <button
                    onClick={() => setActiveSection('all')}
                    className={`segmented-item ${activeSection === 'all' ? 'segmented-item-active' : ''}`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setActiveSection('text')}
                    className={`segmented-item ${activeSection === 'text' ? 'segmented-item-active' : ''}`}
                  >
                    Text
                  </button>
                  <button
                    onClick={() => setActiveSection('choice')}
                    className={`segmented-item ${activeSection === 'choice' ? 'segmented-item-active' : ''}`}
                  >
                    Choices
                  </button>
                  <button
                    onClick={() => setActiveSection('numeric')}
                    className={`segmented-item ${activeSection === 'numeric' ? 'segmented-item-active' : ''}`}
                  >
                    Numeric
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="p-0">
            {surveyDetails.questions && surveyDetails.questions.length > 0 && !((surveyDetails.status === 'open' || surveyDetails.status === 'pending') && !surveyDetails.has_partial_responses) ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr>
                      <th className="th-cell px-6">
                        Question Code
                      </th>
                      <th className="th-cell px-6">
                        Question Type
                      </th>
                      <th className="th-cell px-6">
                        Question
                      </th>
                      <th className="th-cell px-6">
                        Answer
                      </th>
                      <th className="th-cell px-6 w-10">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <AnimatePresence mode="popLayout">
                      {filteredQuestions.map((question, index) => (
                        <React.Fragment key={question.id}>
                          <motion.tr 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className={`tr-interactive ${expandedQuestionId === question.id ? 'bg-gray-50 dark:bg-white/[0.03]' : ''}`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-600 dark:text-primary-400">
                              {/* FECOA-1447: show author-defined question code, falling back to presentation position */}
                              {question.code || `Q${surveyDetails.questions.indexOf(question) + 1}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getQuestionTypeColor(question.type)}`}>
                                {formatQuestionType(question.type)}
                              </span>
                              {question.is_required === false ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-white/[0.06] text-gray-500 dark:text-gray-400 ml-1.5" aria-label="Optional question">
                                  Optional
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300 ml-1.5" aria-label="Required question">
                                  Required
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-md">
                              <div className="truncate" dangerouslySetInnerHTML={{ __html: question.text || '' }} />
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {getFormattedTableAnswer(question)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              <button 
                                onClick={() => toggleExpandQuestion(question.id)}
                                className="rounded-lg p-1.5 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:hover:bg-white/[0.06]"
                              >
                                {expandedQuestionId === question.id ? 
                                  <ChevronUp className="h-4 w-4 text-primary-600 dark:text-primary-400" /> : 
                                  <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                }
                              </button>
                            </td>
                          </motion.tr>
                          {/* Expanded detail row */}
                          {expandedQuestionId === question.id && (
                            <motion.tr 
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="bg-gray-50 dark:bg-white/[0.03]"
                            >
                              <td colSpan={5} className="px-6 py-4">
                                <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
                                  <div className="mb-3">
                                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Detailed Answer</h4>
                                  </div>
                                  {renderDetailedAnswer(question)}
                                </div>
                              </td>
                            </motion.tr>
                          )}
                        </React.Fragment>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 px-6 flex flex-col items-center justify-center">
                <div className="mb-4 rounded-full bg-gray-100 p-4 dark:bg-white/[0.06]">
                  <MessageSquare className="h-7 w-7 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
                </div>
                {surveyDetails.is_suspended ? (
                  <>
                    <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-300 mb-2">Survey Suspended</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-center max-w-md">
                      This survey was suspended due to participant deactivation. No responses were recorded before suspension.
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md mt-2">
                      Any partial responses captured before suspension are saved with item-level timestamps and marked as Partial status.
                    </p>
                  </>
                ) : (surveyDetails.status === 'open' || surveyDetails.status === 'pending') ? (
                  <>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {surveyDetails.status === 'open' ? 'Survey Is Open' : 'Survey Not Yet Started'}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
                      {surveyDetails.status === 'open' 
                        ? 'This survey window is currently open, but no responses have been recorded yet.'
                        : 'This survey is scheduled but not yet available to the participant.'}
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No Responses Available</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
                      This survey doesn't have any recorded responses.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Metadata/Debug section - unobtrusive toggle (shown whenever details load so Study/Site IDs are always available for troubleshooting) */}
        <div className="fixed bottom-12 right-4 z-10" style={{ position: 'fixed' }}>
            {/* Always visible toggle button */}
            <div className="flex items-center mb-2 justify-end">
              <button
                onClick={() => setShowDebugInfo(!showDebugInfo)}
                className="flex items-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600 shadow-card transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                title="Toggle debug information"
              >
                <Info className="h-3.5 w-3.5 mr-1" />
                {showDebugInfo ? 'Hide' : 'Show'} Debug Info
              </button>
            </div>
            
            {/* Collapsible debug panel */}
            <AnimatePresence>
              {showDebugInfo && (
                <motion.div 
                  initial={{ opacity: 0, y: 10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: 10, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="max-h-96 max-w-md overflow-hidden overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-float dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center">
                      <File className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                      <h2 className="text-sm font-medium text-gray-700 dark:text-gray-200">Debug Information</h2>
                    </div>
                    <button
                      onClick={() => setShowDebugInfo(false)}
                      className="rounded-md p-0.5 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="p-4 space-y-4">
                    {/* Core Survey Info */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 uppercase tracking-wide">Core Survey Info</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { label: 'Completed Survey ID', value: surveyDetails.id },
                          { label: 'Survey ID', value: surveyDetails.survey_id },
                          { label: 'Window ID', value: surveyDetails.window_id },
                          { label: 'Survey Name', value: surveyDetails.survey_name },
                          { label: 'Status', value: surveyDetails.status },
                          { label: 'Is Suspended', value: surveyDetails.is_suspended },
                          { label: 'Has Partial Responses', value: surveyDetails.has_partial_responses },
                        ].map(({ label, value }) => (
                          <div key={label} className="rounded-md border border-gray-100 bg-gray-50 p-2 text-xs dark:border-gray-700 dark:bg-gray-900/40">
                            <div className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
                            <div className="text-gray-900 dark:text-gray-100 break-words font-mono">
                              {value !== undefined && value !== null ? String(value) : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Timing */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 uppercase tracking-wide">Timing</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { label: 'Opened At', value: surveyDetails.openedAt },
                          { label: 'Completed At', value: surveyDetails.completedAt },
                          { label: 'Form Open Time', value: surveyDetails.form_open_time },
                          { label: 'Last Response Time', value: surveyDetails.last_response_time },
                          { label: 'Duration (seconds)', value: surveyDetails.duration_seconds },
                        ].map(({ label, value }) => (
                          <div key={label} className="rounded-md border border-gray-100 bg-gray-50 p-2 text-xs dark:border-gray-700 dark:bg-gray-900/40">
                            <div className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
                            <div className="text-gray-900 dark:text-gray-100 break-words font-mono">
                              {value !== undefined && value !== null ? String(value) : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Participant */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 uppercase tracking-wide">Participant</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { label: 'Participant ID', value: surveyDetails.participant_id },
                          { label: 'Participant Human ID', value: surveyDetails.participant_human_id },
                        ].map(({ label, value }) => (
                          <div key={label} className="rounded-md border border-gray-100 bg-gray-50 p-2 text-xs dark:border-gray-700 dark:bg-gray-900/40">
                            <div className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
                            <div className="text-gray-900 dark:text-gray-100 break-words font-mono">
                              {value !== undefined && value !== null ? String(value) : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Study & Site — business codes (DB) + internal PKs for engineering */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 uppercase tracking-wide">Study & Site</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { label: 'Study code', value: resolvedStudyCodeForDebug },
                          { label: 'Site code', value: resolvedSiteCodeForDebug },
                          { label: 'Study ID (internal)', value: resolvedStudyIdForDebug },
                          { label: 'Site ID (internal)', value: resolvedSiteIdForDebug },
                        ].map(({ label, value }) => (
                          <div key={label} className="rounded-md border border-gray-100 bg-gray-50 p-2 text-xs dark:border-gray-700 dark:bg-gray-900/40">
                            <div className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
                            <div className="text-gray-900 dark:text-gray-100 break-words font-mono">
                              {value !== undefined && value !== null ? String(value) : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Response Stats */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 uppercase tracking-wide">Response Stats</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { label: 'Total Questions', value: surveyDetails.questions?.length },
                          { label: 'Responses Count', value: surveyDetails.responses ? Object.keys(surveyDetails.responses).length : 0 },
                        ].map(({ label, value }) => (
                          <div key={label} className="rounded-md border border-gray-100 bg-gray-50 p-2 text-xs dark:border-gray-700 dark:bg-gray-900/40">
                            <div className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
                            <div className="text-gray-900 dark:text-gray-100 break-words font-mono">
                              {value !== undefined && value !== null ? String(value) : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Additional Metadata */}
                    {surveyDetails.metadata && Object.keys(surveyDetails.metadata).length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 uppercase tracking-wide">Additional Metadata</h3>
                        <div className="grid grid-cols-1 gap-2">
                          {Object.entries(surveyDetails.metadata).map(([key, value]) => (
                            <div key={key} className="rounded-md border border-gray-100 bg-gray-50 p-2 text-xs dark:border-gray-700 dark:bg-gray-900/40">
                              <div className="font-medium text-gray-500 dark:text-gray-400 mb-0.5 capitalize">
                                {key.replace(/_/g, ' ')}
                              </div>
                              <div className="text-gray-900 dark:text-gray-100 break-words font-mono">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
      </div>
    </div>
  );
};

export default CompletedSurveyDetails;